import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import GameTimer from 'app/components/GameTimer';
import JerseyIcon from 'app/icons/jersey.svg';
import WhistleIcon from 'app/icons/whistle.svg';
import FootballIcon from 'app/icons/football.svg';
import FlagIcon from 'app/icons/flag.svg';

import style from './style.module.scss';

interface Props {
  game?: GameESoccer;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
}

const halfScore = (opp: ESoccerGameOpponent | undefined, halfIndex: number) =>
  opp?.halves.find(h => h.index === halfIndex)?.score ?? 0;

const ESoccerScoreboard: FC<Props> = ({ game, opponent1, opponent2 }) => {
  const { t } = useTranslation();
  const isRunning = game?.status === 'running';
  const isFinished = game?.status === 'finished';
  const isNotStarted = !game || game.status === 'not_started';

  const opp1Stats = game?.opponents.find(o => o.id === opponent1?.id);
  const opp2Stats = game?.opponents.find(o => o.id === opponent2?.id);

  const firstHalfPlaying = isRunning && game?.current_half === 1;
  const secondHalfPlaying = isRunning && game?.current_half === 2;
  const showSecondHalfScore = game?.current_half === 2 || isFinished;

  return (
    <div className={style.esoccerScoreboard}>
      <div className={classNames(style.scoreDisplay, { [style.preMatch]: isNotStarted })}>
        <div className={style.playerSide}>
          <span className={style.playerName}>{opponent1?.name}</span>
          {opp1Stats?.ingame_team_name && (
            <span className={style.teamName}>
              {opp1Stats.ingame_team_name}
              <JerseyIcon className={style.jerseyIcon} />
            </span>
          )}
        </div>

        {isNotStarted ? (
          <span className={style.vsLabel}>vs</span>
        ) : (
          <>
            <span
              className={classNames(style.goalScore, {
                [style.losing]:
                  isFinished && game?.winner_id !== null && game?.winner_id !== opponent1?.id
              })}
            >
              {opp1Stats?.score ?? 0}
            </span>

            <div
              className={classNames(style.timerBlock, {
                [style.timerBlockActive]: isRunning && game?.timer
              })}
            >
              {isRunning && game?.timer ? (
                <>
                  <GameTimer timer={game.timer} videogame="e-soccer" live className={style.timer} />
                  {game.current_half !== null && (
                    <span className={style.halfLabel}>
                      {game.current_half === 1
                        ? t('esoccer-first_half', '1ST HALF')
                        : t('esoccer-second_half', '2ND HALF')}
                    </span>
                  )}
                </>
              ) : (
                <span className={style.scoreSeparator}>-</span>
              )}
            </div>

            <span
              className={classNames(style.goalScore, {
                [style.losing]:
                  isFinished && game?.winner_id !== null && game?.winner_id !== opponent2?.id
              })}
            >
              {opp2Stats?.score ?? 0}
            </span>
          </>
        )}

        <div className={classNames(style.playerSide, style.rightSide)}>
          <span className={style.playerName}>{opponent2?.name}</span>
          {opp2Stats?.ingame_team_name && (
            <span className={style.teamName}>
              <JerseyIcon className={style.jerseyIcon} />
              {opp2Stats.ingame_team_name}
            </span>
          )}
        </div>
      </div>

      {!isNotStarted && (
        <div className={style.timeline}>
          <WhistleIcon className={style.timelineIcon} />
          <div className={style.halfSection}>
            <div className={classNames(style.halfBar, { [style.active]: firstHalfPlaying })} />
            <div className={classNames(style.halftimeMarker, { [style.active]: firstHalfPlaying })}>
              <FootballIcon className={style.ballIcon} />
              <span className={style.halftimeScore}>
                {halfScore(opp1Stats, 1)} - {halfScore(opp2Stats, 1)}
              </span>
            </div>
            <div className={classNames(style.halfBar, { [style.active]: firstHalfPlaying })} />
          </div>
          <div className={style.halfSeparator} />
          <div className={style.halfSection}>
            <div className={classNames(style.halfBar, { [style.active]: secondHalfPlaying })} />
            {showSecondHalfScore && (
              <>
                <div
                  className={classNames(style.halftimeMarker, {
                    [style.active]: secondHalfPlaying
                  })}
                >
                  <FootballIcon className={style.ballIcon} />
                  <span className={style.halftimeScore}>
                    {halfScore(opp1Stats, 2)} - {halfScore(opp2Stats, 2)}
                  </span>
                </div>
                <div className={classNames(style.halfBar, { [style.active]: secondHalfPlaying })} />
              </>
            )}
          </div>
          <FlagIcon className={style.timelineIcon} />
        </div>
      )}
    </div>
  );
};

ESoccerScoreboard.displayName = 'ESoccerScoreboard';

export default ESoccerScoreboard;
